import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { BellRinging, BellSlash, Warning, ArrowClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ensurePushSubscription, isPushSupported, pushPermission, sendTestPush, resetAllPushSubs } from "@/lib/webPush";

/**
 * v1.25.61 — Always-visible Notification Status card on the customer portal.
 * Replaces the silent auto-subscribe that was failing invisibly on real
 * customer phones (production had `active_subscribers: 0` even after deploy).
 *
 * Shows the customer:
 *   - Whether their browser even supports push
 *   - Whether they've granted OS permission
 *   - Whether MICM's backend has an active subscription for them
 *   - The EXACT reason if subscribe just failed
 * And gives them big, obvious buttons: "Enable notifications" (subscribe /
 * re-subscribe) and "Send test" (proves the pipeline).
 */
const REASON_LABELS = {
    "denied": "Blocked in your browser settings — tap the padlock in the address bar and switch Notifications to Allow.",
    "browser-unsupported": "This browser doesn't support push. Use Chrome/Edge on Android or Safari 16.4+ on iOS.",
    "server-not-configured": "MICM's server-side push isn't configured yet. Ask your admin to check /api/health/push.",
    "no-push-manager": "Push disabled at your phone's OS level. Check notification permissions for your browser app.",
    "permission-default": "Please tap Allow when the browser prompts.",
    "subscribe-failed": "Subscribe failed on this device.",
    "subscribed": "You'll get a phone alert on every verify / payment.",
    "not-subscribed": "Your phone hasn't subscribed yet — tap Enable below.",
};

// Show the actual error string when available so "unknown" never appears.
function reasonMessage(reason, error) {
    const base = REASON_LABELS[reason] || `Couldn't enable notifications (${reason || "unknown"})`;
    return error ? `${base} · ${error}` : base;
}

export default function NotificationStatusCard() {
    const [supported] = useState(isPushSupported());
    const [perm, setPerm] = useState(pushPermission());
    const [backendState, setBackendState] = useState({ known: false, active: false, checking: true });
    const [lastReason, setLastReason] = useState(null);
    const [busy, setBusy] = useState(false);
    // v1.25.68 — Live diagnostic: prove — from the customer's own screen —
    // whether the push actually arrived at THIS phone, even when Android
    // suppresses the notification banner.
    const [swVersion, setSwVersion] = useState(null);
    const [lastPushArrived, setLastPushArrived] = useState(null);

    const refreshBackend = useCallback(async () => {
        try {
            const r = await api.get("/push/status");
            setBackendState({ known: true, active: !!r.data?.active, checking: false, endpoint: r.data?.endpoint });
        } catch {
            setBackendState({ known: false, active: false, checking: false });
        }
    }, []);

    useEffect(() => {
        if (!supported) return;
        setPerm(pushPermission());
        refreshBackend();
    }, [supported, refreshBackend]);

    // v1.25.68 — Ask the active SW its version and listen for PUSH_RECEIVED
    // broadcasts. The SW posts one message per incoming push (with the
    // payload summary and any showNotification error), so the customer can
    // literally see "Push landed at 3:14:22 PM" even if Android's foreground
    // heuristic ate the banner.
    useEffect(() => {
        if (!supported || !("serviceWorker" in navigator)) return;
        let cancelled = false;
        (async () => {
            try {
                const reg = await navigator.serviceWorker.ready;
                const sw = reg.active || reg.installing || reg.waiting;
                if (sw) sw.postMessage({ type: "GET_SW_VERSION" });
            } catch { /* noop */ }
        })();
        const onMsg = (event) => {
            const d = event.data || {};
            if (cancelled) return;
            if (d.type === "SW_VERSION_REPLY") {
                setSwVersion(d.version || "unknown");
            } else if (d.type === "PUSH_RECEIVED") {
                setLastPushArrived({
                    at: d.receivedAt || Date.now(),
                    title: d.title,
                    body: d.body,
                    showError: d.showError || null,
                });
                // v1.25.69 — Never steal the fancy 3D Thank-You moment for a
                // real verify/payment push. Only surface a toast for TEST
                // pushes or genuine display errors; the diagnostic strip in
                // the card below still lights up either way, so the customer
                // gets full transparency without losing the celebration.
                const isThanksPush = !!(d.meta && d.meta.thanks) && !(d.meta && d.meta.test);
                if (d.showError) {
                    toast.error(`Push arrived but the OS couldn't show it: ${d.showError}`);
                } else if (isThanksPush) {
                    // Fire the same event the notifications-poll uses so the
                    // FancyThanks overlay opens even when the app is already
                    // in the foreground during the push. v1.25.70 — pass the
                    // payload's `kind` (e.g. "payout") so the overlay picks
                    // the correct copy ("Payment received" vs "Slip uploaded").
                    const kind = (d.meta && d.meta.kind)
                        || (d.meta && d.meta.payout ? "payout" : "verified");
                    try {
                        window.dispatchEvent(new CustomEvent("micm-thanks-fire", {
                            detail: { kind, meta: d.meta || {} },
                        }));
                    } catch { /* noop */ }
                } else {
                    // Test push (or generic) — safe to toast.
                    toast.success(`Push landed on this phone · "${d.title || "MICM"}"`);
                }
            }
        };
        navigator.serviceWorker.addEventListener("message", onMsg);
        return () => {
            cancelled = true;
            navigator.serviceWorker.removeEventListener("message", onMsg);
        };
    }, [supported]);

    // First-visit best-effort auto-subscribe (silent). If it fails, we still
    // show the card so the customer can retry manually.
    useEffect(() => {
        if (!supported) return;
        (async () => {
            if (Notification.permission === "granted" && !backendState.active && !backendState.checking) {
                const r = await ensurePushSubscription({ silent: true });
                if (r?.ok) {
                    setLastReason("subscribed");
                    await refreshBackend();
                } else if (r?.reason) {
                    setLastReason(r.reason);
                }
            }
        })();
    }, [supported, backendState.checking, backendState.active, refreshBackend]);

    const enable = async () => {
        setBusy(true);
        // v1.25.66 — Always force-fresh so a silently-dead endpoint gets
        // replaced with a brand-new one on the very first tap.
        const r = await ensurePushSubscription({ silent: false, forceFresh: true });
        setBusy(false);
        setPerm(pushPermission());
        setLastReason(r?.reason || (r?.ok ? "subscribed" : "subscribe-failed"));
        if (r?.ok) {
            toast.success("Notifications enabled — you'll get an alert on every verify.");
            await refreshBackend();
        } else {
            toast.error(reasonMessage(r?.reason, r?.error));
        }
    };

    const resetAndResubscribe = async () => {
        setBusy(true);
        toast.message("Clearing your old device subscriptions…");
        const reset = await resetAllPushSubs();
        if (!reset?.ok) {
            setBusy(false);
            toast.error(reset?.error || "Couldn't reset. Try again.");
            return;
        }
        const re = await ensurePushSubscription({ silent: false, forceFresh: true });
        setBusy(false);
        await refreshBackend();
        if (re?.ok) {
            toast.success(`Cleared ${reset.removed} old device${reset.removed === 1 ? "" : "s"} and registered this one. Try 'Send test' now.`);
        } else {
            toast.error(reasonMessage(re?.reason, re?.error));
        }
    };

    const test = async () => {
        setBusy(true);
        const r = await sendTestPush();
        if (r?.sent > 0) {
            setBusy(false);
            toast.success("Test push sent — check your phone in a few seconds.");
            return;
        }
        // v1.25.66 — If the backend reports the current endpoint is stale,
        // force a truly fresh subscription (unsubscribe + subscribe) and
        // retry once. This breaks the loop where getSubscription() kept
        // returning the same dead endpoint.
        if (r?.stale) {
            toast.message("Refreshing your push subscription…");
            const re = await ensurePushSubscription({ silent: false, forceFresh: true });
            if (re?.ok) {
                await refreshBackend();
                const retry = await sendTestPush();
                setBusy(false);
                if (retry?.sent > 0) {
                    toast.success("Test push sent — check your phone!");
                } else {
                    toast.error(retry?.detail || retry?.error || "Still no delivery. Tap 'Reset devices' to clear all old subscriptions and try again.");
                }
                return;
            }
            setBusy(false);
            await refreshBackend();
            toast.error(REASON_LABELS[re?.reason] || re?.error || "Couldn't refresh your subscription. Tap 'Reset devices' to start clean.");
            return;
        }
        setBusy(false);
        // Refresh so the UI reflects the true backend state (active may
        // have flipped to false after this call deactivated stale subs).
        await refreshBackend();
        toast.error(r?.detail || r?.error || "No devices subscribed yet — enable first.");
    };

    if (!supported) {
        return (
            <div data-testid="notif-status-card" className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                Push notifications aren't supported in this browser.
            </div>
        );
    }

    // States (in priority order)
    const isOk = perm === "granted" && backendState.active;
    const needsAction = perm !== "granted" || !backendState.active;
    const isBlocked = perm === "denied";

    return (
        <div
            data-testid="notif-status-card"
            className={`rounded-lg border px-4 py-3 shadow-sm ${
                isOk ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50" :
                isBlocked ? "border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50" :
                "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50"
            }`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${
                        isOk ? "bg-emerald-500" : isBlocked ? "bg-rose-500" : "bg-amber-500"
                    }`}>
                        {isOk ? <BellRinging size={18} weight="fill" />
                            : isBlocked ? <BellSlash size={18} weight="fill" />
                            : <Warning size={18} weight="fill" className="animate-pulse" />}
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900" data-testid="notif-status-title">
                            {isOk ? "Notifications ON" : isBlocked ? "Notifications BLOCKED" : "Notifications OFF"}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-700">
                            <span className="mr-2">Browser: <b>{perm}</b></span>
                            <span>Server sub: <b data-testid="notif-status-active">{backendState.checking ? "checking…" : backendState.active ? "active" : "none"}</b></span>
                        </div>
                        {lastReason && REASON_LABELS[lastReason] && (
                            <div className="mt-1 text-[11px] leading-relaxed text-slate-600" data-testid="notif-status-reason">
                                {REASON_LABELS[lastReason]}
                            </div>
                        )}
                        {/* v1.25.68 — Ground-truth diagnostic strip */}
                        {(swVersion || lastPushArrived) && (
                            <div
                                data-testid="notif-status-diag"
                                className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] leading-tight text-slate-500"
                            >
                                {swVersion && (
                                    <span data-testid="notif-status-sw-version">
                                        SW <b>{swVersion}</b>
                                    </span>
                                )}
                                {lastPushArrived && (
                                    <span
                                        data-testid="notif-status-last-push"
                                        className={lastPushArrived.showError ? "text-rose-600" : "text-emerald-700"}
                                    >
                                        {lastPushArrived.showError
                                            ? `⚠ Push arrived but display failed: ${lastPushArrived.showError}`
                                            : `✓ Push landed at ${new Date(lastPushArrived.at).toLocaleTimeString()}`}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!isOk && !isBlocked && (
                        <button
                            type="button"
                            data-testid="notif-status-enable"
                            disabled={busy}
                            onClick={enable}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-60"
                        >
                            {busy ? "Enabling…" : "Enable notifications"}
                        </button>
                    )}
                    {isOk && (
                        <>
                            <button
                                type="button"
                                data-testid="notif-status-refresh"
                                disabled={busy}
                                onClick={enable}
                                title="Re-subscribe this phone with a fresh endpoint"
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
                            >
                                <ArrowClockwise size={12} weight="bold" /> Re-sub
                            </button>
                            <button
                                type="button"
                                data-testid="notif-status-reset"
                                disabled={busy}
                                onClick={resetAndResubscribe}
                                title="Clear every stale sub on the server and start fresh from this browser"
                                className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                            >
                                Reset devices
                            </button>
                            <button
                                type="button"
                                data-testid="notif-status-test"
                                disabled={busy}
                                onClick={test}
                                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                            >
                                <PaperPlaneTilt size={12} weight="bold" /> Send test
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
